/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import type { Gradient, HostContext, Logger, MosaicType, RGBColor, Stop, TileValue } from '../@types';
import https from 'https';
import getConfig from '../config';
import RenditionService from './RenditionService';
import { LIBRARY_MIMETYPE_MAP } from '../containers/SearchResultsPage/SearchConstants';
import { getErrorMessage } from '../utils/getErrorMessage';
import { getLogger } from '../utils/SearchLogger';
import GradientUtils from '../utils/GradientUtils';
type GradientStopData = {
	offset: number;
	midpoint?: number;
};
type ColorStopData = GradientStopData & {
	color: ColorData[];
};
type OpacityStopData = GradientStopData & {
	opacity: number;
};
// LibraryGradient and Gradient are different
// LibraryGradient -> returned from library rendition service
// Gradient -> returned from search server
type LibraryGradient = {
	type: 'linear' | 'radial';
	stops: ColorStopData[];
	angle?: number;
	scale?: number;
	aspectRatio?: number;
	centerPoint?: {
		x: number;
		y: number;
	};
	opacities?: OpacityStopData[];
};
type ColorData = {
	mode: string;
	value: RGBColor;
};
type Swatch = Array<ColorData>;
type Swatches = {
	swatches: Array<Swatch>;
};
type RenditionType = string | Swatches;
type TileObject = {
	type: string;
	rendition: RenditionType;
};
// Global Declarations
const MAX_TILES = 4;
const MAX_COLOR_THEME_COLORS = 5;
export default class LibraryMosaicService {
	logger: Logger;
	searchHostContext: HostContext;

	constructor(searchHostContext: HostContext) {
		this.logger = getLogger();
		this.searchHostContext = searchHostContext;
	}

	/*
	 **
	 **  P U B L I C   I N T E R F A C E
	 **
	 */

	/**
	 * [getLibraryMosaicTiles retrieves data for the mosaic rendition Id]
	 * @returns object - mosaic rendition datarequest response object
	 */
	async getLibraryMosaicTiles(libraryUrn: string): Promise<MosaicType> {
		const mosaicData = await this._performLibraryMosaicRenditionDataRequest(libraryUrn);
		const tiles: MosaicType = await this._getMosaicTiles(mosaicData);
		return tiles;
	}

	/**
	 * [getLocalLibraryMosaicTiles retrieves tiles data for the mosaicData]
	 * @returns object - mosaic rendition datarequest response object
	 */
	getLocalLibraryMosaicTiles(mosaicData: Record<string, any>): MosaicType {
		const tiles: MosaicType = this._getLocalMosaicTiles(mosaicData);
		return tiles;
	}

	/*
	 **
	 **  P R I V A T E   M E T H O D S
	 **
	 */

	/*

   Sample response:-
  	{
  	"id": "347e08da-d9e4-49d3-b358-5c1f350efcdd",
  	"name": "bike library own 5",
  	"creator": "XXXX@AdobeID",
  	"ownership": "private",
  	"created_date": 1560927995474,
  	"modified_date": 1563558730174,
  	"version": "26",
  	"region": "us",
  	"library_urn": "urn:aaid:sc:us:8bb2fa8b-ac5d-4a26-8657-5818370452b4",
  	"manifest_urn": "urn:aaid:sc:us:c5762b64-10a2-49a2-bd71-2df341d470ff",
  	"storage_path": "/assets/adobe-libraries/347e08da-d9e4-49d3-b358-5c1f350efcdd",
  	"elements_count": 4,
  	"removed_elements_count": 4,
  	"rendition_grid": {
  		"_fc": true,
  		"main": {
  			"type": "href",
  			"rendition": "https://cc-api-storage-stage.adobe.io/assets/adobe-libraries/347e08da-d9e4-49d3-b358-5c1f350efcdd/9f695ef9-275a-4a36-9299-041d049c6caf/:rendition;size=200"
  		},
  		"tiles": [
  			{
  				"type": "href",
  				"rendition": "https://cc-api-storage-stage.adobe.io/assets/adobe-libraries/347e08da-d9e4-49d3-b358-5c1f350efcdd/0a96c374-c737-4ab4-8e69-c922c71de18f/:rendition;size=200"
  			},
  			{
  				"type": "application/vnd.adobe.element.colortheme+dcx",
  				"rendition": {
  					"swatches": [
  						[
  							{
  								"mode": "RGB",
  								"profile_name": "sRGB IEC61966-2.1",
  								"type": "process",
  								"value": {
  									"r": 255.0,
  									"b": 255.0,
  									"g": 0.0
  								}
  							}
  						],
  						[
  							{
  								"mode": "RGB",
  								"profile_name": "sRGB IEC61966-2.1",
  								"type": "process",
  								"value": {
  									"r": 178.0,
  									"b": 178.0,
  									"g": 0.0
  								}
  							}
  						],
  						[
  							{
  								"mode": "RGB",
  								"profile_name": "sRGB IEC61966-2.1",
  								"type": "process",
  								"value": {
  									"r": 255.0,
  									"b": 26.0,
  									"g": 187.0
  								}
  							}
  						],
  						[
  							{
  								"mode": "RGB",
  								"profile_name": "sRGB IEC61966-2.1",
  								"type": "process",
  								"value": {
  									"r": 0.0,
  									"b": 141.0,
  									"g": 204.0
  								}
  							}
  						],
  						[
  							{
  								"mode": "RGB",
  								"profile_name": "sRGB IEC61966-2.1",
  								"type": "process",
  								"value": {
  									"r": 0.0,
  									"b": 141.0,
  									"g": 204.0
  								}
  							}
  						]
  					]
  				}
  			},
  			{
  				"type": "application/vnd.adobe.element.color+dcx",
  				"rendition": {
  					"swatches": [
  						[
  							{
  								"mode": "RGB",
  								"profile_name": "sRGB IEC61966-2.1",
  								"library_rel": "primary",
  								"type": "spot",
  								"value": {
  									"r": 51.0,
  									"b": 164.0,
  									"g": 214.0
  								}
  							}
  						]
  					]
  				}
  			}
  		]
  	},
  	"_links": {
  		"http://ns.adobe.com/melville/rel/primary": {
  			"href": "/api/v1/libraries/urn:aaid:sc:us:8bb2fa8b-ac5d-4a26-8657-5818370452b4",
  			"rel": "http://ns.adobe.com/melville/rel/primary",
  			"type": "application.json",
  			"templated": false
  		},
  		"http://ns.adobe.com/melville/rel/path": {
  			"href": "/api/v1/libraries/urn:aaid:sc:us:8bb2fa8b-ac5d-4a26-8657-5818370452b4",
  			"rel": "http://ns.adobe.com/melville/rel/path",
  			"type": "application.json",
  			"templated": false
  		},
  		"http://ns.adobe.com/melville/rel/id": {
  			"href": "/api/v1/libraries/urn:aaid:sc:us:8bb2fa8b-ac5d-4a26-8657-5818370452b4",
  			"rel": "http://ns.adobe.com/melville/rel/id",
  			"type": "application.json",
  			"templated": false
  		},
  		"http://ns.adobe.com/melville/rel/describedBy": {
  			"href": "/api/v1/libraries/urn:aaid:sc:us:8bb2fa8b-ac5d-4a26-8657-5818370452b4",
  			"rel": "http://ns.adobe.com/melville/rel/describedBy",
  			"type": "application.json",
  			"templated": false
  		},
  		"http://ns.adobe.com/melville/rel/metadata/rendition": {
  			"href": "/api/v1/libraries/urn:aaid:sc:us:8bb2fa8b-ac5d-4a26-8657-5818370452b4?selector=rendition_grid",
  			"rel": "http://ns.adobe.com/melville/rel/metadata/rendition",
  			"type": "application.json",
  			"templated": false
  		},
  		"http://ns.adobe.com/melville/rel/elements": {
  			"href": "/api/v1/libraries/urn:aaid:sc:us:8bb2fa8b-ac5d-4a26-8657-5818370452b4/elements",
  			"rel": "http://ns.adobe.com/melville/rel/elements",
  			"type": "application.json",
  			"templated": false
  		},
  		"http://ns.adobe.com/melville/rel/element": {
  			"href": "/api/v1/libraries/urn:aaid:sc:us:8bb2fa8b-ac5d-4a26-8657-5818370452b4/elements/{elementId}",
  			"rel": "http://ns.adobe.com/melville/rel/element",
  			"type": "application.json",
  			"templated": true
  		},
  		"http://ns.adobe.com/melville/rel/groups": {
  			"href": "/api/v1/libraries/urn:aaid:sc:us:8bb2fa8b-ac5d-4a26-8657-5818370452b4/groups",
  			"rel": "http://ns.adobe.com/melville/rel/groups",
  			"type": "application.json",
  			"templated": false
  		}
  	},
  	"assetSubType": "library",
  	"_fc": false
  }
  */

	/**
	 * Retrieves  data for the library mosaic rendition from calling the library urn.
	 * @param {libraryUrn} library urn
	 * @returns {Object}
	 */
	_performLibraryMosaicRenditionDataRequest(libraryUrn: string): Promise<Record<string, any>> {
		return new Promise((resolve, reject) => {
			const request = https.request(
				{
					hostname: getConfig(this.searchHostContext.environment, 'LIBRARY_SERVER'),
					path: `/api/v1/libraries/${libraryUrn}?selector=rendition_grid`,
					method: 'GET',
					headers: {
						'x-api-key': this.searchHostContext.hostInfo.apiKey,
						authorization: `Bearer ${this.searchHostContext.imsInfo.accessToken}`,
					},
				},
				(res: any) => {
					if (res.statusCode >= 300) {
						const libraryMosaicServiceError = new Error(
							`Problem with Mosaic Rendition fetch response: ${res.statusCode}`,
						);
						reject(libraryMosaicServiceError);
					} else {
						let data = '';
						// Set the encoding to UTF8 to prevent decoding issues on certain browsers.
						res.setEncoding('utf8');
						res.on('data', (chunk) => (data += chunk));
						res.on('end', () => {
							// Success! :-)
							try {
								resolve(JSON.parse(data));
							} catch (e) {
								// Error! :-(
								const libraryMosaicServiceError = new Error(
									`Failed parsing of JSON response in Mosaic Service: ${getErrorMessage(e)}`,
								);
								reject(libraryMosaicServiceError);
							}
						});
					}
				},
			);
			request.on('error', (err) => {
				// On error, display the search error message :-(
				const libraryMosaicServiceError = new Error(
					`Problem with Mosaic Rendition fetch request: ${getErrorMessage(err)}`,
				);
				reject(libraryMosaicServiceError);
			});
			request.end();
		});
	}

	async _getMosaicTiles(mosaicData: Record<string, any>): Promise<MosaicType> {
		const tiles: Array<TileValue> = [];

		if (mosaicData.rendition_grid) {
			if (mosaicData.rendition_grid.main) {
				const tile: TileValue | null | undefined = await this._getMosaicElement(mosaicData.rendition_grid.main);

				// Check if we get valid tile data
				if (tile) {
					tiles.push(tile);
				}
			}

			if (mosaicData.rendition_grid.tiles && Array.isArray(mosaicData.rendition_grid.tiles)) {
				for (const tileObject of mosaicData.rendition_grid.tiles) {
					const tile: TileValue | null | undefined = await this._getMosaicElement(tileObject);

					// Check if we get valid tile data
					if (tile) {
						tiles.push(tile);
					}
				}
			}
		}

		// Return MAX_TILES number of tiles
		return {
			type: 'mosaic',
			value: {
				tiles: tiles.slice(0, MAX_TILES),
			},
		};
	}

	/**
	 * Fetches mosaic element rendition blob for libraries
	 */
	async _fetchMosaicElementRenditionBlob(url: string): Promise<string | null> {
		const renditionService = new RenditionService(this.searchHostContext);
		let mosaicElementRenditionBlob: string | null = null;

		try {
			mosaicElementRenditionBlob = (await renditionService.fetchBinary(url)) as string;
		} catch (e) {
			getLogger().warn('Library Mosaic Service', 'Failed to fetch mosaic element rendition blob', e);
		}

		return mosaicElementRenditionBlob;
	}

	/**
	 * Creates and returns an object of mosaic element rendition data.
	 * @returns {Object}
	 */
	async _getMosaicElement(tileObject: TileObject): Promise<TileValue | null | undefined> {
		let tileValue: TileValue | null | undefined = null;

		switch (tileObject.type) {
			case 'href':
				{
					// FOR FLOW: perform type checking for Flow inference
					if (typeof tileObject.rendition === 'string') {
						const mosaicElementRenditionBlob = await this._fetchMosaicElementRenditionBlob(
							tileObject.rendition,
						);

						if (mosaicElementRenditionBlob) {
							tileValue = {
								type: 'image',
								image: mosaicElementRenditionBlob,
							};
						}
					}
				}
				break;

			case LIBRARY_MIMETYPE_MAP.LIBRARY_ELEMENT_COLOR:
				// FOR FLOW: perform type checking for Flow inference
				if (
					typeof tileObject.rendition === 'object' &&
					tileObject.rendition.swatches &&
					Array.isArray(tileObject.rendition.swatches)
				) {
					const swatches: Array<Swatch> = tileObject.rendition.swatches;
					tileValue = this._getMosaicColor(swatches);
				}

				break;

			case LIBRARY_MIMETYPE_MAP.LIBRARY_ELEMENT_COLORTHEME:
				// Get all the RGB data
				// FOR FLOW: perform type checking for Flow inference
				if (
					typeof tileObject.rendition === 'object' &&
					tileObject.rendition.swatches &&
					Array.isArray(tileObject.rendition.swatches)
				) {
					const swatches: Array<Swatch> = tileObject.rendition.swatches;
					tileValue = this._getMosaicColorTheme(swatches);
				}

				break;

			case LIBRARY_MIMETYPE_MAP.LIBRARY_ELEMENT_GRADIENT:
				if (typeof tileObject.rendition === 'object') {
					tileValue = this._getMosaicGradient(tileObject.rendition as any as LibraryGradient);
				}

				break;

			default:
				this.logger.error('Your Work Library Card', 'Unknown Tile Type:', tileObject.type);
		}

		return tileValue;
	}

	/*

   Sample response:-
  	{
  	"id": "347e08da-d9e4-49d3-b358-5c1f350efcdd",
  	"name": "bike library own 5",
  	"creator": "XXXX@AdobeID",
  	"ownership": "private",
  	"created_date": 1560927995474,
  	"modified_date": 1563558730174,
  	"version": "26",
  	"region": "us",
  	"library_urn": "urn:aaid:sc:us:8bb2fa8b-ac5d-4a26-8657-5818370452b4",
  	"manifest_urn": "urn:aaid:sc:us:c5762b64-10a2-49a2-bd71-2df341d470ff",
  	"storage_path": "/assets/adobe-libraries/347e08da-d9e4-49d3-b358-5c1f350efcdd",
  	"elements_count": 4,
  	"removed_elements_count": 4,
  	"rendition_grid": {
  		"_fc": true,
  		"main": {
  			"type": "href",
  			"rendition": "https://cc-api-storage-stage.adobe.io/assets/adobe-libraries/347e08da-d9e4-49d3-b358-5c1f350efcdd/9f695ef9-275a-4a36-9299-041d049c6caf/:rendition;size=200"
  		},
  		"tiles": [
  			{
  				"type": "href",
  				"rendition": "https://cc-api-storage-stage.adobe.io/assets/adobe-libraries/347e08da-d9e4-49d3-b358-5c1f350efcdd/0a96c374-c737-4ab4-8e69-c922c71de18f/:rendition;size=200"
  			},
  			{
  				"type": "application/vnd.adobe.element.colortheme+dcx",
  				"rendition": {
  					"swatches": [
  						[
  							{
  								"mode": "RGB",
  								"profile_name": "sRGB IEC61966-2.1",
  								"type": "process",
  								"value": {
  									"r": 255.0,
  									"b": 255.0,
  									"g": 0.0
  								}
  							}
  						],
  						[
  							{
  								"mode": "RGB",
  								"profile_name": "sRGB IEC61966-2.1",
  								"type": "process",
  								"value": {
  									"r": 178.0,
  									"b": 178.0,
  									"g": 0.0
  								}
  							}
  						],
  						[
  							{
  								"mode": "RGB",
  								"profile_name": "sRGB IEC61966-2.1",
  								"type": "process",
  								"value": {
  									"r": 255.0,
  									"b": 26.0,
  									"g": 187.0
  								}
  							}
  						],
  						[
  							{
  								"mode": "RGB",
  								"profile_name": "sRGB IEC61966-2.1",
  								"type": "process",
  								"value": {
  									"r": 0.0,
  									"b": 141.0,
  									"g": 204.0
  								}
  							}
  						],
  						[
  							{
  								"mode": "RGB",
  								"profile_name": "sRGB IEC61966-2.1",
  								"type": "process",
  								"value": {
  									"r": 0.0,
  									"b": 141.0,
  									"g": 204.0
  								}
  							}
  						]
  					]
  				}
  			},
  			{
  				"type": "application/vnd.adobe.element.color+dcx",
  				"rendition": {
  					"swatches": [
  						[
  							{
  								"mode": "RGB",
  								"profile_name": "sRGB IEC61966-2.1",
  								"library_rel": "primary",
  								"type": "spot",
  								"value": {
  									"r": 51.0,
  									"b": 164.0,
  									"g": 214.0
  								}
  							}
  						]
  					]
  				}
  			}
  		]
  	},
  	"_links": {
  		"http://ns.adobe.com/melville/rel/primary": {
  			"href": "/api/v1/libraries/urn:aaid:sc:us:8bb2fa8b-ac5d-4a26-8657-5818370452b4",
  			"rel": "http://ns.adobe.com/melville/rel/primary",
  			"type": "application.json",
  			"templated": false
  		},
  		"http://ns.adobe.com/melville/rel/path": {
  			"href": "/api/v1/libraries/urn:aaid:sc:us:8bb2fa8b-ac5d-4a26-8657-5818370452b4",
  			"rel": "http://ns.adobe.com/melville/rel/path",
  			"type": "application.json",
  			"templated": false
  		},
  		"http://ns.adobe.com/melville/rel/id": {
  			"href": "/api/v1/libraries/urn:aaid:sc:us:8bb2fa8b-ac5d-4a26-8657-5818370452b4",
  			"rel": "http://ns.adobe.com/melville/rel/id",
  			"type": "application.json",
  			"templated": false
  		},
  		"http://ns.adobe.com/melville/rel/describedBy": {
  			"href": "/api/v1/libraries/urn:aaid:sc:us:8bb2fa8b-ac5d-4a26-8657-5818370452b4",
  			"rel": "http://ns.adobe.com/melville/rel/describedBy",
  			"type": "application.json",
  			"templated": false
  		},
  		"http://ns.adobe.com/melville/rel/metadata/rendition": {
  			"href": "/api/v1/libraries/urn:aaid:sc:us:8bb2fa8b-ac5d-4a26-8657-5818370452b4?selector=rendition_grid",
  			"rel": "http://ns.adobe.com/melville/rel/metadata/rendition",
  			"type": "application.json",
  			"templated": false
  		},
  		"http://ns.adobe.com/melville/rel/elements": {
  			"href": "/api/v1/libraries/urn:aaid:sc:us:8bb2fa8b-ac5d-4a26-8657-5818370452b4/elements",
  			"rel": "http://ns.adobe.com/melville/rel/elements",
  			"type": "application.json",
  			"templated": false
  		},
  		"http://ns.adobe.com/melville/rel/element": {
  			"href": "/api/v1/libraries/urn:aaid:sc:us:8bb2fa8b-ac5d-4a26-8657-5818370452b4/elements/{elementId}",
  			"rel": "http://ns.adobe.com/melville/rel/element",
  			"type": "application.json",
  			"templated": true
  		},
  		"http://ns.adobe.com/melville/rel/groups": {
  			"href": "/api/v1/libraries/urn:aaid:sc:us:8bb2fa8b-ac5d-4a26-8657-5818370452b4/groups",
  			"rel": "http://ns.adobe.com/melville/rel/groups",
  			"type": "application.json",
  			"templated": false
  		}
  	},
  	"assetSubType": "library",
  	"_fc": false
  }
  */
	_getLocalMosaicTiles(mosaicData: Record<string, any>): MosaicType {
		const tiles: Array<TileValue> = [];

		if (mosaicData.rendition_grid) {
			if (mosaicData.rendition_grid.main) {
				const tile: TileValue | null | undefined = this._getLocalMosaicElement(mosaicData.rendition_grid.main);

				// Check if we get valid tile data
				if (tile) {
					tiles.push(tile);
				}
			}

			if (mosaicData.rendition_grid.tiles && Array.isArray(mosaicData.rendition_grid.tiles)) {
				for (const tileObject of mosaicData.rendition_grid.tiles) {
					const tile: TileValue | null | undefined = this._getLocalMosaicElement(tileObject);

					// Check if we get valid tile data
					if (tile) {
						tiles.push(tile);
					}
				}
			}
		}

		// Return MAX_TILES number of tiles
		return {
			type: 'mosaic',
			value: {
				tiles: tiles.slice(0, MAX_TILES),
			},
		};
	}

	/**
	 * Creates and returns an object of mosaic element rendition data.
	 * @returns {Object}
	 */
	_getLocalMosaicElement(tileObject: TileObject): TileValue | null | undefined {
		let tileValue: TileValue | null | undefined = null;

		switch (tileObject.type) {
			case 'href':
				{
					// FOR FLOW: perform type checking for Flow inference
					if (typeof tileObject.rendition === 'string') {
						if (tileObject.rendition) {
							tileValue = {
								type: 'image',
								image: `file://${tileObject.rendition}`,
							};
						}
					}
				}
				break;

			case LIBRARY_MIMETYPE_MAP.LIBRARY_ELEMENT_COLOR:
				// FOR FLOW: perform type checking for Flow inference
				if (
					typeof tileObject.rendition === 'object' &&
					tileObject.rendition.swatches &&
					Array.isArray(tileObject.rendition.swatches)
				) {
					const swatches: Array<Swatch> = tileObject.rendition.swatches;
					tileValue = this._getMosaicColor(swatches);
				}

				break;

			case LIBRARY_MIMETYPE_MAP.LIBRARY_ELEMENT_COLORTHEME:
				// FOR FLOW: perform type checking for Flow inference
				if (
					typeof tileObject.rendition === 'object' &&
					tileObject.rendition.swatches &&
					Array.isArray(tileObject.rendition.swatches)
				) {
					const swatches: Array<Swatch> = tileObject.rendition.swatches;
					tileValue = this._getMosaicColorTheme(swatches);
				}

				break;

			case LIBRARY_MIMETYPE_MAP.LIBRARY_ELEMENT_GRADIENT:
				if (typeof tileObject.rendition === 'object') {
					tileValue = this._getMosaicGradient(tileObject.rendition as any as LibraryGradient);
				}

				break;

			default:
				this.logger.error('Your Work Library Card', 'Unknown Tile Type:', tileObject.type);
		}

		return tileValue;
	}

	/**
	 * Creates and returns an object of mosaic color rendition data.
	 * @returns {Object}
	 */
	_getMosaicColor(swatches: Array<Swatch>): TileValue | null | undefined {
		let tileValue: TileValue | null | undefined = null;

		/**
		 * Sample data:-
		 * {
		 *	"swatches": [
		 *		[
		 *		{
		 *			"mode": "RGB",
		 *			"profile_name": "",
		 *			"library_rel": "primary",
		 *			"type": "process",
		 *			"value": {
		 *			"r": 215,
		 *			"b": 35,
		 *			"g": 223
		 *			}
		 *		}
		 *			]
		 *		]
		 *	}
		 */
		// FOR FLOW: perform type checking for Flow inference
		if (Array.isArray(swatches)) {
			if (swatches[0] && Array.isArray(swatches[0])) {
				const colorArray = swatches[0]
					.map((item) => {
						let RGBValue: RGBColor | null = null;

						if (item?.mode === 'RGB') {
							RGBValue = item.value;
						}

						return RGBValue;
					})
					.filter((item) => item !== null);

				if (colorArray && Array.isArray(colorArray) && colorArray[0]) {
					tileValue = {
						type: 'color',
						color: {
							r: colorArray[0].r,
							g: colorArray[0].g,
							b: colorArray[0].b,
						},
					};
					return tileValue;
				}
			}
		}
	}

	/**
	 * Creates and returns an object of mosaic color theme rendition data.
	 * @returns {Object}
	 */
	_getMosaicColorTheme(swatches: Array<Swatch>): TileValue | null | undefined {
		let tileValue: TileValue | null | undefined = null;

		/**
		 * Sample data:-
		 * {
		 *	"swatches": [
		 *		[
		 *		{
		 *			"mode": "RGB",
		 *			"profile_name": "sRGB IEC61966-2.1",
		 *			"type": "process",
		 *			"value": {
		 *			"r": 194,
		 *			"b": 146,
		 *			"g": 40
		 *			}
		 *		}
		 *		],
		 *		[
		 *		{
		 *			"mode": "RGB",
		 *			"profile_name": "sRGB IEC61966-2.1",
		 *			"type": "process",
		 *			"value": {
		 *			"r": 194,
		 *			"b": 161,
		 *			"g": 89
		 *			}
		 *		}
		 *		],
		 *		[
		 *		{
		 *			"mode": "RGB",
		 *			"profile_name": "sRGB IEC61966-2.1",
		 *			"type": "process",
		 *			"value": {
		 *			"r": 194,
		 *			"b": 176,
		 *			"g": 137
		 *			}
		 *		}
		 *		],
		 *		[
		 *		{
		 *			"mode": "RGB",
		 *			"profile_name": "sRGB IEC61966-2.1",
		 *			"type": "process",
		 *			"value": {
		 *			"r": 194,
		 *			"b": 137,
		 *			"g": 11
		 *			}
		 *		}
		 *		],
		 *		[
		 *		{
		 *			"mode": "RGB",
		 *			"profile_name": "sRGB IEC61966-2.1",
		 *			"type": "process",
		 *			"value": {
		 *			"r": 194,
		 *			"b": 137,
		 *			"g": 11
		 *			}
		 *		}
		 *		]
		 *	]
		 *	}
		 */
		// Get all the RGB data
		// FOR FLOW: perform type checking for Flow inference
		if (Array.isArray(swatches)) {
			const colorSwatches: Array<RGBColor> = [];
			swatches.forEach((swatchArray) => {
				if (Array.isArray(swatchArray)) {
					const colorsArray = swatchArray
						.map((item) => {
							let RGBValue: RGBColor | null = null;

							if (item?.mode === 'RGB') {
								RGBValue = item.value;
							}

							return RGBValue;
						})
						.filter((item) => item !== null);

					if (colorsArray && Array.isArray(colorsArray) && colorsArray[0]) {
						const color: RGBColor = colorsArray[0];
						colorSwatches.push({
							r: color.r,
							g: color.g,
							b: color.b,
						});
					}
				}
			});
			tileValue = {
				type: 'colorTheme',
				colors: colorSwatches.slice(0, MAX_COLOR_THEME_COLORS),
			};
			return tileValue;
		}
	}

	/**
	 * Formats and returns LibraryGradient type data to Gradient type data which is used for gradient rendition
	 * @param  {LibraryGradient} data
	 * @returns Gradient
	 */
	_getMosaicGradient(data: LibraryGradient): Gradient | null {
		try {
			const gradientType = `${data.type}Gradient`;
			// @ts-ignore
			delete data.type;
			const colorStops: Array<Stop> = [];
			// The color data is not as per schema of Gradient
			// translate it to match the types
			// use only if color mode is RGB
			data.stops.forEach((dataColorStop: ColorStopData) => {
				const { mode, value: colorValue } = dataColorStop.color[0];
				const colorMode = mode.toUpperCase();
				colorMode === 'RGB' &&
					colorStops.push({
						offset: dataColorStop.offset,
						color: colorValue,
					});
			});
			const gradientData: any = {
				type: gradientType,
				value: { ...data, stops: colorStops },
			};

			if (GradientUtils.isGradientData(gradientData)) {
				return gradientData as Gradient;
			}

			return null;
		} catch (e) {
			getLogger().warn('Library Mosaic Service', 'Failed to parse gradient data', e);
			return null;
		}
	}
}
